import {FormHelperText} from '@mui/material';
import {getHours, getMinutes, isValid, parse} from 'date-fns'; // ✅ Use date-fns
import React from 'react';
import {Controller, ControllerProps, FieldPath, FieldValues, RegisterOptions, ValidationRule} from 'react-hook-form';
import {AppTimePicker, AppTimePickerProps} from './AppTimePicker';

export type AppControlledTimePickerProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<AppTimePickerProps, 'name' | 'required'> &
    Omit<ControllerProps<TFieldValues, TName>, 'render'> & {
        required?: string | ValidationRule<boolean>;
        label: string | React.ReactNode;
        errorMessage?: string;
    };

export function AppControlledTimePicker<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    name,
    control,
    rules,
    onChange,
    required,
    label,
    errorMessage,
    ...rest
}: AppControlledTimePickerProps<TFieldValues, TName>) {
    const customRules: Omit<
        RegisterOptions<TFieldValues, TName>,
        'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'
    > = rules ?? {required: required ? `${label} is required` : false};

    return (
        <Controller
            name={name}
            rules={customRules}
            control={control}
            render={({field, fieldState: {invalid, error}}) => {
                return (
                    <>
                        <AppTimePicker
                            label={label}
                            required={!!required}
                            {...rest}
                            {...field}
                            value={field.value ? parse(field.value, 'HH:mm', new Date()) : null}
                            onChange={(e, context) => {
                                if (isValid(e)) {
                                    if (e) {
                                        const durationInMinutes = getHours(e) * 60 + getMinutes(e);
                                        field.onChange(durationInMinutes);
                                        if (onChange) onChange(e, context);
                                    } else {
                                        field.onChange(null);
                                        if (onChange) onChange(null, context);
                                    }
                                } else {
                                    field.onChange(null);
                                    if (onChange) onChange(null, context);
                                }
                            }}
                        />
                        <FormHelperText sx={{color: 'red'}} error={invalid}>
                            {errorMessage ?? error?.message ?? ''}
                        </FormHelperText>
                    </>
                );
            }}
        />
    );
}

export default AppControlledTimePicker;
